import {
  RestoreSession,
  SignIn,
  SignOut,
} from '@application/use-cases/auth';
import {
  CreateAppointment,
  DeleteAppointment,
  SearchAppointments,
  UpdateAppointment,
} from '@application/use-cases/appointments';
import {
  CreateCompany,
  DeleteCompany,
  GetCompany,
  ModerateCompanies,
  SearchCompanies,
  UpdateCompany,
} from '@application/use-cases/companies';
import {
  CreateOffer,
  DeleteOffer,
  GetOffer,
  ModerateOffers,
  SearchOffers,
  SuspendOffer,
  UpdateOffer,
} from '@application/use-cases/offers';
import {
  DeleteArticle,
  SaveArticle,
  SearchArticles,
  SetArticleStatus,
} from '@application/use-cases/articles';
import {
  ConfirmAddressImport,
  DeleteAddressReview,
  DeleteAddresses,
  DeleteReview,
  DismissReview,
  GetAddressReviews,
  ListFlaggedReviews,
  PreviewAddressImport,
  SaveAddress,
  SearchAddresses,
  SetAddressesActive,
} from '@application/use-cases/addresses';
import {
  DeleteAppUser,
  ExportAppUsers,
  CreateAppUser,
  SearchAppUsers,
  SetAppUserStatus,
  UpdateAppUser,
} from '@application/use-cases/users';
import {
  ListAdmins,
  SaveAdmin,
  SetAdminStatus,
  ToggleAdminRole,
} from '@application/use-cases/admins';
import {
  DeleteArticleCategory,
  DeleteCountry,
  DeleteFormula,
  GetMobileConfig,
  GetNotificationTemplates,
  GetReminderSettings,
  ListArticleCategories,
  ListCities,
  ListCountries,
  ListFormulas,
  ListNotificationLog,
  SaveArticleCategory,
  SaveCountry,
  SaveFormula,
  SaveMobileConfig,
  SaveNotificationTemplate,
  SaveReminderSettings,
  ToggleArticleCategory,
  ToggleCountry,
} from '@application/use-cases/settings';
import {
  ListPregnancyWeeks,
  GetPregnancyWeek,
  SavePregnancyWeek,
  ValidatePregnancyWeek,
} from '@application/use-cases/pregnancy';
import {
  GetPerformanceTotals,
  SearchCompanyPerformance,
  SearchUsageJournal,
} from '@application/use-cases/performance';
import {
  DeleteAppointmentReason,
  DeleteBusinessType,
  DeleteDirectoryCategory,
  DeleteOfferCategory,
  ListAppointmentReasons,
  ListBusinessTypes,
  ListDirectoryCategories,
  ListOfferCategories,
  SaveAppointmentReason,
  SaveBusinessType,
  SaveDirectoryCategory,
  SaveOfferCategory,
  ToggleAppointmentReason,
  ToggleBusinessType,
  ToggleDirectoryCategory,
  ToggleOfferCategory,
} from '@application/use-cases/reference-data';
import { GetDashboardSnapshot } from '@application/use-cases/dashboard';
import { SearchAuditLogs } from '@application/use-cases/audit-log';
import type {
  AddressRepository,
  AdminRepository,
  AppointmentRepository,
  AppointmentReasonRepository,
  AppUserRepository,
  ArticleCategoryRepository,
  ArticleRepository,
  AuditLogRepository,
  AuthRepository,
  BusinessTypeRepository,
  CityRepository,
  CompanyRepository,
  DirectoryCategoryRepository,
  CountryRepository,
  FormulaRepository,
  MobileConfigRepository,
  NotificationRepository,
  OfferCategoryRepository,
  OfferRepository,
  PerformanceRepository,
  PregnancyRepository,
  ReviewRepository,
} from '@domain/repositories';
import type { AuditLogPage, MobileConfig } from '@domain/entities';
import { HttpClient } from '../http/http-client';
import { InMemoryDb } from '../repositories/in-memory/in-memory-db';
import {
  InMemoryAppointmentRepository,
  InMemoryArticleRepository,
  InMemoryAuthRepository,
  InMemoryCompanyRepository,
  InMemoryOfferRepository,
} from '../repositories/in-memory/catalog-repositories';
import {
  InMemoryAddressRepository,
  InMemoryAdminRepository,
  InMemoryAppUserRepository,
  InMemoryReviewRepository,
} from '../repositories/in-memory/directory-repositories';
import {
  InMemoryCityRepository,
  InMemoryCountryRepository,
  InMemoryFormulaRepository,
  InMemoryNotificationRepository,
  InMemoryPerformanceRepository,
  InMemoryPregnancyRepository,
} from '../repositories/in-memory/settings-repositories';
import {
  HttpAddressRepository,
  HttpAdminRepository,
  HttpAppointmentRepository,
  HttpAppointmentReasonRepository,
  HttpAppUserRepository,
  HttpArticleCategoryRepository,
  HttpArticleRepository,
  HttpAuditLogRepository,
  HttpAuthRepository,
  HttpBusinessTypeRepository,
  HttpCityRepository,
  HttpCompanyRepository,
  HttpCountryRepository,
  HttpDirectoryCategoryRepository,
  HttpFormulaRepository,
  HttpMobileConfigRepository,
  HttpNotificationRepository,
  HttpOfferCategoryRepository,
  HttpOfferRepository,
  HttpPerformanceRepository,
  HttpPregnancyRepository,
  HttpReviewRepository,
  clearAccessToken,
  readAccessToken,
} from '../repositories/http/http-repositories';
import { BrowserFileExporter, SessionAccessPolicy } from './adapters';

interface Repositories {
  auth: AuthRepository;
  appointments: AppointmentRepository;
  cities: CityRepository;
  companies: CompanyRepository;
  offers: OfferRepository;
  articles: ArticleRepository;
  addresses: AddressRepository;
  reviews: ReviewRepository;
  users: AppUserRepository;
  admins: AdminRepository;
  formulas: FormulaRepository;
  countries: CountryRepository;
  pregnancy: PregnancyRepository;
  notifications: NotificationRepository;
  performance: PerformanceRepository;
  articleCategories: ArticleCategoryRepository;
  appointmentReasons: AppointmentReasonRepository;
  businessTypes: BusinessTypeRepository;
  directoryCategories: DirectoryCategoryRepository;
  offerCategories: OfferCategoryRepository;
  auditLog: AuditLogRepository;
  mobileConfig: MobileConfigRepository;
  media: {
    requestUpload(input: { purpose: string; extension: string; contentType: string }): Promise<{ uploadUrl: string; key: string }>;
  };
}

/** Objet exposé à la présentation : uniquement des cas d'usage, jamais de repository. */
export interface Container {
  policy: SessionAccessPolicy;
  auth: { signIn: SignIn; signOut: SignOut; restoreSession: RestoreSession };
  cities: { list: ListCities };
  dashboard: { snapshot: GetDashboardSnapshot };
  appointments: {
    search: SearchAppointments;
    create: CreateAppointment;
    update: UpdateAppointment;
    remove: DeleteAppointment;
  };
  companies: {
    create: CreateCompany;
    search: SearchCompanies;
    get: GetCompany;
    moderate: ModerateCompanies;
    update: UpdateCompany;
    remove: DeleteCompany;
  };
  offers: {
    create: CreateOffer;
    search: SearchOffers;
    get: GetOffer;
    moderate: ModerateOffers;
    suspend: SuspendOffer;
    update: UpdateOffer;
    delete: DeleteOffer;
  };
  articles: {
    search: SearchArticles;
    save: SaveArticle;
    setStatus: SetArticleStatus;
    remove: DeleteArticle;
  };
  addresses: {
    search: SearchAddresses;
    save: SaveAddress;
    remove: DeleteAddresses;
    setActive: SetAddressesActive;
    getReviews: GetAddressReviews;
    deleteAddressReview: DeleteAddressReview;
    previewImport: PreviewAddressImport;
    confirmImport: ConfirmAddressImport;
    listReviews: ListFlaggedReviews;
    dismissReview: DismissReview;
    deleteReview: DeleteReview;
  };
  users: {
    create: CreateAppUser;
    search: SearchAppUsers;
    update: UpdateAppUser;
    setStatus: SetAppUserStatus;
    exportCsv: ExportAppUsers;
    remove: DeleteAppUser;
  };
  admins: {
    list: ListAdmins;
    save: SaveAdmin;
    setStatus: SetAdminStatus;
    toggleRole: ToggleAdminRole;
  };
  settings: {
    listFormulas: ListFormulas;
    saveFormula: SaveFormula;
    deleteFormula: DeleteFormula;
    getReminder: GetReminderSettings;
    saveReminder: SaveReminderSettings;
    listCountries: ListCountries;
    saveCountry: SaveCountry;
    toggleCountry: ToggleCountry;
    deleteCountry: DeleteCountry;
    getTemplates: GetNotificationTemplates;
    saveTemplate: SaveNotificationTemplate;
    listLog: ListNotificationLog;
    getMobileConfig: GetMobileConfig;
    saveMobileConfig: SaveMobileConfig;
  };
  articleCategories: {
    list: ListArticleCategories;
    save: SaveArticleCategory;
    toggle: ToggleArticleCategory;
    remove: DeleteArticleCategory;
  };
  appointmentReasons: {
    list: ListAppointmentReasons;
    save: SaveAppointmentReason;
    toggle: ToggleAppointmentReason;
    remove: DeleteAppointmentReason;
  };
  businessTypes: {
    list: ListBusinessTypes;
    save: SaveBusinessType;
    toggle: ToggleBusinessType;
    remove: DeleteBusinessType;
  };
  offerCategories: {
    list: ListOfferCategories;
    save: SaveOfferCategory;
    toggle: ToggleOfferCategory;
    remove: DeleteOfferCategory;
  };
  directoryCategories: {
    list: ListDirectoryCategories;
    save: SaveDirectoryCategory;
    toggle: ToggleDirectoryCategory;
    remove: DeleteDirectoryCategory;
  };
  pregnancy: {
    list: ListPregnancyWeeks;
    get: GetPregnancyWeek;
    save: SavePregnancyWeek;
    validate: ValidatePregnancyWeek;
  };
  performance: {
    search: SearchCompanyPerformance;
    totals: GetPerformanceTotals;
    journal: SearchUsageJournal;
  };
  auditLog: { search: SearchAuditLogs };
  media: {
    requestUpload(input: { purpose: string; extension: string; contentType: string }): Promise<{ uploadUrl: string; key: string }>;
  };
}

function inMemoryRepositories(): Repositories {
  const db = new InMemoryDb();
  return {
    auth: new InMemoryAuthRepository(),
    appointments: new InMemoryAppointmentRepository(db),
    cities: new InMemoryCityRepository(),
    companies: new InMemoryCompanyRepository(db),
    offers: new InMemoryOfferRepository(db),
    articles: new InMemoryArticleRepository(db),
    addresses: new InMemoryAddressRepository(db),
    reviews: new InMemoryReviewRepository(db),
    users: new InMemoryAppUserRepository(db),
    admins: new InMemoryAdminRepository(db),
    formulas: new InMemoryFormulaRepository(db),
    countries: new InMemoryCountryRepository(db),
    pregnancy: new InMemoryPregnancyRepository(db),
    notifications: new InMemoryNotificationRepository(db),
    performance: new InMemoryPerformanceRepository(db),
    articleCategories: {
      async list() { return []; },
      async create(draft: Parameters<ArticleCategoryRepository['create']>[0]) { return { id: crypto.randomUUID(), ...draft, isActive: true }; },
      async update(id: string, patch: Parameters<ArticleCategoryRepository['update']>[1]) { return { id, slug: '', label: '', sortOrder: 0, isActive: true, ...patch }; },
      async toggleActive(id: string, currentIsActive: boolean) { return { id, slug: '', label: '', sortOrder: 0, isActive: !currentIsActive }; },
      async remove() {},
    },
    appointmentReasons: {
      async list() { return []; },
      async create(draft: Parameters<AppointmentReasonRepository['create']>[0]) { return { id: crypto.randomUUID(), ...draft, isActive: true }; },
      async update(id: string, patch: Parameters<AppointmentReasonRepository['update']>[1]) { return { id, slug: '', label: '', sortOrder: 0, isActive: true, ...patch }; },
      async toggleActive(id: string, currentIsActive: boolean) { return { id, slug: '', label: '', sortOrder: 0, isActive: !currentIsActive }; },
      async remove() {},
    },
    businessTypes: {
      async list() { return []; },
      async create(draft: Parameters<BusinessTypeRepository['create']>[0]) { return { id: crypto.randomUUID(), ...draft, isActive: true }; },
      async update(id: string, patch: Parameters<BusinessTypeRepository['update']>[1]) { return { id, slug: '', label: '', sortOrder: 0, isActive: true, ...patch }; },
      async toggleActive(id: string, currentIsActive: boolean) { return { id, slug: '', label: '', sortOrder: 0, isActive: !currentIsActive }; },
      async remove() {},
    },
    offerCategories: {
      async list() { return []; },
      async create(draft: Parameters<OfferCategoryRepository['create']>[0]) { return { id: crypto.randomUUID(), ...draft, isActive: true }; },
      async update(id: string, patch: Parameters<OfferCategoryRepository['update']>[1]) { return { id, slug: '', label: '', sortOrder: 0, isActive: true, ...patch }; },
      async toggleActive(id: string, currentIsActive: boolean) { return { id, slug: '', label: '', sortOrder: 0, isActive: !currentIsActive }; },
      async remove() {},
    },
    directoryCategories: {
      async list() { return []; },
      async create(draft: Parameters<DirectoryCategoryRepository['create']>[0]) { return { id: crypto.randomUUID(), ...draft, isActive: true }; },
      async update(id: string, patch: Parameters<DirectoryCategoryRepository['update']>[1]) { return { id, slug: '', label: '', sortOrder: 0, isActive: true, ...patch }; },
      async toggleActive(id: string, currentIsActive: boolean) { return { id, slug: '', label: '', sortOrder: 0, isActive: !currentIsActive }; },
      async remove() {},
    },
    auditLog: { async search(): Promise<AuditLogPage> { return { data: [], total: 0, page: 1, pageSize: 20, pageCount: 0 }; } },
    mobileConfig: {
      async get(): Promise<MobileConfig> {
        return { iosMinVersion: '1.0.0', androidMinVersion: '1.0.0', iosForceUpdate: false, androidForceUpdate: false };
      },
      async save(config: MobileConfig): Promise<MobileConfig> {
        return config;
      },
    },
    media: {
      async requestUpload(input: { purpose: string; extension: string; contentType: string }): Promise<{ uploadUrl: string; key: string }> {
        const key = `local/${input.purpose}-${Date.now()}.${input.extension}`;
        return { uploadUrl: '', key };
      },
    },
  };
}

function httpRepositories(baseUrl: string, onUnauthenticated: () => void): Repositories {
  const http = new HttpClient({
    baseUrl,
    getAccessToken: readAccessToken,
    onUnauthenticated: () => {
      clearAccessToken();
      onUnauthenticated();
    },
  });
  const mediaRepo = {
    async requestUpload(input: { purpose: string; extension: string; contentType: string }): Promise<{ uploadUrl: string; key: string }> {
      const res = await http.post<{ uploadUrl: string; key: string; expiresInSec: number }>(
        '/admin/media/upload-url',
        input,
      );
      return { uploadUrl: res.uploadUrl, key: res.key };
    },
  };

  return {
    auth: new HttpAuthRepository(http),
    appointments: new HttpAppointmentRepository(http),
    cities: new HttpCityRepository(http),
    companies: new HttpCompanyRepository(http),
    offers: new HttpOfferRepository(http),
    articles: new HttpArticleRepository(http),
    addresses: new HttpAddressRepository(http),
    reviews: new HttpReviewRepository(http),
    users: new HttpAppUserRepository(http),
    admins: new HttpAdminRepository(http),
    formulas: new HttpFormulaRepository(http),
    countries: new HttpCountryRepository(http),
    pregnancy: new HttpPregnancyRepository(http),
    notifications: new HttpNotificationRepository(http),
    performance: new HttpPerformanceRepository(http),
    articleCategories: new HttpArticleCategoryRepository(http),
    appointmentReasons: new HttpAppointmentReasonRepository(http),
    businessTypes: new HttpBusinessTypeRepository(http),
    directoryCategories: new HttpDirectoryCategoryRepository(http),
    offerCategories: new HttpOfferCategoryRepository(http),
    auditLog: new HttpAuditLogRepository(http),
    mobileConfig: new HttpMobileConfigRepository(http),
    media: mediaRepo,
  };
}

export interface ContainerOptions {
  dataSource?: 'in-memory' | 'http';
  apiBaseUrl?: string;
  onUnauthenticated?: () => void;
}

/**
 * Point de composition unique. La présentation ne construit jamais
 * de repository : elle consomme uniquement ce conteneur.
 */
export function createContainer(options: ContainerOptions = {}): Container {
  const dataSource =
    options.dataSource ??
    ((import.meta.env.VITE_DATA_SOURCE as 'in-memory' | 'http' | undefined) ?? 'in-memory');
  const baseUrl =
    options.apiBaseUrl ?? (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

  const repos =
    dataSource === 'http'
      ? httpRepositories(baseUrl, options.onUnauthenticated ?? (() => undefined))
      : inMemoryRepositories();

  const policy = new SessionAccessPolicy();
  const exporter = new BrowserFileExporter();

  return {
    policy,
    auth: {
      signIn: new SignIn(repos.auth),
      signOut: new SignOut(repos.auth),
      restoreSession: new RestoreSession(repos.auth),
    },
    cities: {
      list: new ListCities(repos.cities),
    },
    dashboard: {
      snapshot: new GetDashboardSnapshot(
        repos.companies,
        repos.offers,
        repos.articles,
        repos.users,
        repos.reviews,
      ),
    },
    appointments: {
      search: new SearchAppointments(repos.appointments),
      create: new CreateAppointment(repos.appointments),
      update: new UpdateAppointment(repos.appointments),
      remove: new DeleteAppointment(repos.appointments),
    },
    companies: {
      create: new CreateCompany(repos.companies),
      search: new SearchCompanies(repos.companies),
      get: new GetCompany(repos.companies),
      moderate: new ModerateCompanies(repos.companies),
      update: new UpdateCompany(repos.companies, policy),
      remove: new DeleteCompany(repos.companies, policy),
    },
    offers: {
      create: new CreateOffer(repos.offers),
      search: new SearchOffers(repos.offers),
      get: new GetOffer(repos.offers),
      moderate: new ModerateOffers(repos.offers, repos.notifications),
      suspend: new SuspendOffer(repos.offers, policy),
      update: new UpdateOffer(repos.offers),
      delete: new DeleteOffer(repos.offers, policy),
    },
    articles: {
      search: new SearchArticles(repos.articles),
      save: new SaveArticle(repos.articles),
      setStatus: new SetArticleStatus(repos.articles, repos.notifications),
      remove: new DeleteArticle(repos.articles, policy),
    },
    addresses: {
      search: new SearchAddresses(repos.addresses),
      save: new SaveAddress(repos.addresses),
      remove: new DeleteAddresses(repos.addresses, policy),
      setActive: new SetAddressesActive(repos.addresses),
      getReviews: new GetAddressReviews(repos.addresses),
      deleteAddressReview: new DeleteAddressReview(repos.addresses, policy),
      previewImport: new PreviewAddressImport(repos.addresses),
      confirmImport: new ConfirmAddressImport(repos.addresses),
      listReviews: new ListFlaggedReviews(repos.reviews),
      dismissReview: new DismissReview(repos.reviews),
      deleteReview: new DeleteReview(repos.reviews, policy),
    },
    users: {
      create: new CreateAppUser(repos.users),
      search: new SearchAppUsers(repos.users),
      update: new UpdateAppUser(repos.users),
      setStatus: new SetAppUserStatus(repos.users, policy),
      exportCsv: new ExportAppUsers(repos.users, exporter),
      remove: new DeleteAppUser(repos.users, policy),
    },
    admins: {
      list: new ListAdmins(repos.admins, policy),
      save: new SaveAdmin(repos.admins, policy),
      setStatus: new SetAdminStatus(repos.admins, policy),
      toggleRole: new ToggleAdminRole(repos.admins, policy),
    },
    settings: {
      listFormulas: new ListFormulas(repos.formulas),
      saveFormula: new SaveFormula(repos.formulas, policy),
      deleteFormula: new DeleteFormula(repos.formulas, policy),
      getReminder: new GetReminderSettings(repos.formulas),
      saveReminder: new SaveReminderSettings(repos.formulas, policy),
      listCountries: new ListCountries(repos.countries),
      saveCountry: new SaveCountry(repos.countries, policy),
      toggleCountry: new ToggleCountry(repos.countries, policy),
      deleteCountry: new DeleteCountry(repos.countries, policy),
      getTemplates: new GetNotificationTemplates(repos.notifications),
      saveTemplate: new SaveNotificationTemplate(repos.notifications),
      listLog: new ListNotificationLog(repos.notifications),
      getMobileConfig: new GetMobileConfig(repos.mobileConfig),
      saveMobileConfig: new SaveMobileConfig(repos.mobileConfig, policy),
    },
    pregnancy: {
      list: new ListPregnancyWeeks(repos.pregnancy),
      get: new GetPregnancyWeek(repos.pregnancy),
      save: new SavePregnancyWeek(repos.pregnancy),
      validate: new ValidatePregnancyWeek(repos.pregnancy),
    },
    performance: {
      search: new SearchCompanyPerformance(repos.performance),
      totals: new GetPerformanceTotals(repos.performance),
      journal: new SearchUsageJournal(repos.performance),
    },
    auditLog: { search: new SearchAuditLogs(repos.auditLog) },
    articleCategories: {
      list: new ListArticleCategories(repos.articleCategories),
      save: new SaveArticleCategory(repos.articleCategories, policy),
      toggle: new ToggleArticleCategory(repos.articleCategories, policy),
      remove: new DeleteArticleCategory(repos.articleCategories, policy),
    },
    appointmentReasons: {
      list: new ListAppointmentReasons(repos.appointmentReasons),
      save: new SaveAppointmentReason(repos.appointmentReasons, policy),
      toggle: new ToggleAppointmentReason(repos.appointmentReasons, policy),
      remove: new DeleteAppointmentReason(repos.appointmentReasons, policy),
    },
    businessTypes: {
      list: new ListBusinessTypes(repos.businessTypes),
      save: new SaveBusinessType(repos.businessTypes, policy),
      toggle: new ToggleBusinessType(repos.businessTypes, policy),
      remove: new DeleteBusinessType(repos.businessTypes, policy),
    },
    offerCategories: {
      list: new ListOfferCategories(repos.offerCategories),
      save: new SaveOfferCategory(repos.offerCategories, policy),
      toggle: new ToggleOfferCategory(repos.offerCategories, policy),
      remove: new DeleteOfferCategory(repos.offerCategories, policy),
    },
    directoryCategories: {
      list: new ListDirectoryCategories(repos.directoryCategories),
      save: new SaveDirectoryCategory(repos.directoryCategories, policy),
      toggle: new ToggleDirectoryCategory(repos.directoryCategories, policy),
      remove: new DeleteDirectoryCategory(repos.directoryCategories, policy),
    },
    media: {
      requestUpload: (input) => repos.media.requestUpload(input),
    },
  };
}
